/**
 * Per-tool gating for the deep-analysis tools (M8). Every tool is gated by BOTH:
 *
 *   (a) a white-label BUILD-GO flag (`brand.analysisTools.<tool>`) — all OFF by
 *       default so the wedge ships without any analysis tool; and
 *   (b) the M7 ENTITLEMENT check before the tool opens.
 *
 * HARD INVARIANT (mirrors quota.ts): the entitlement input is TIER-derived
 * (`Entitlement.isPaid`), never a consumption/API/render-call count. This module
 * gates on who the account IS (its storage-keyed tier), never on how much it has
 * used. Pure and synchronous — the resolved brand flags + entitlement flow in as
 * props; this shapes the yes/no + the reason.
 */

import type { AnalysisTool, AnalysisToolFlags } from "@/config/brand.schema";
import { capabilityGate, type CapabilityBlock, type TierEntitlement } from "@/lib/billing/tierEntitlement";

/** Tier-derived entitlement (the shared gate primitive; `Entitlement` satisfies it). */
export type AnalysisEntitlement = TierEntitlement;

export type AnalysisToolBlock = CapabilityBlock;

export interface AnalysisToolGate {
  tool: AnalysisTool;
  /** Build-go: is the tool present in the UI at all? (false → fully absent). */
  present: boolean;
  /** present AND entitled — the tool may open. */
  openable: boolean;
  /** Why it can't open (or null when it can). */
  blockedReason: AnalysisToolBlock;
}

/** Tier-derived entitlement decision — the ONE place analysis reads "may use". */
export function isAnalysisEntitled(entitlement: AnalysisEntitlement): boolean {
  return entitlement.isPaid;
}

/** Gate one tool given its build-go flag and the account's entitlement. */
export function analysisToolGate(
  tool: AnalysisTool,
  flags: AnalysisToolFlags,
  entitlement: AnalysisEntitlement,
): AnalysisToolGate {
  const { present, openable, blockedReason } = capabilityGate(flags[tool] === true, entitlement);
  return { tool, present, openable, blockedReason };
}

/** Gate every tool at once — the map the UI iterates to decide what to show. */
export function analysisToolGates(
  flags: AnalysisToolFlags,
  entitlement: AnalysisEntitlement,
): Record<AnalysisTool, AnalysisToolGate> {
  const tools: AnalysisTool[] = ["radius", "territoriesLasso", "territoriesAutomated", "territoriesBoundary", "driveTime", "routes"];
  const out = {} as Record<AnalysisTool, AnalysisToolGate>;
  for (const tool of tools) out[tool] = analysisToolGate(tool, flags, entitlement);
  return out;
}

/** True when at least one tool is present (build-go on) — whether to mount the panel at all. */
export function anyToolPresent(flags: AnalysisToolFlags): boolean {
  return Object.values(flags).some((v) => v === true);
}
